import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import api, { apiErrorText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";
import WalletHistorySheet from "../components/WalletHistorySheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { fmtDateShort, fmtUSD } from "../lib/format";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Settings2,
  X,
  Link2,
  MoreHorizontal,
  History,
  ArrowDownLeft,
  ArrowUpRight,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "",
  email: "",
  number: "",
  number_location: "",
  card_linked: false,
  card_number: "",
  group: "",
  location: "",
  spending_limit: 0,
  details: "",
  status: "active",
  balance: 0,
  notes: "",
  hit_limit: false,
};

// Stable hash → color for groups
const GROUP_COLORS = [
  { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700" },
  { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700" },
  { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
  { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700" },
  { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700" },
  { bg: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-700" },
  { bg: "bg-fuchsia-50", border: "border-fuchsia-200", text: "text-fuchsia-700" },
  { bg: "bg-lime-50", border: "border-lime-200", text: "text-lime-700" },
];
function colorFor(s) {
  if (!s) return null;
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return GROUP_COLORS[Math.abs(h) % GROUP_COLORS.length];
}

const STATUS_TONE = {
  active: "bg-emerald-50 border-emerald-200 text-emerald-700",
  inactive: "bg-zinc-100 border-zinc-200 text-zinc-600",
  frozen: "bg-sky-50 border-sky-200 text-sky-700",
  banned: "bg-rose-50 border-rose-200 text-rose-700",
  pending: "bg-amber-50 border-amber-200 text-amber-700",
};

export default function Chimes() {
  const [list, setList] = useState([]);
  const [opts, setOpts] = useState({
    groups: [],
    locations: [],
    number_locations: [],
    statuses: ["active"],
    limits: [],
  });
  const [loading, setLoading] = useState(true);
  const loadedOnceRef = useRef(false);

  // Filters
  const [filters, setFilters] = useState({
    q: "",
    cashappLocation: "",
    group: "all",
    location: "all",
    status: "all",
    minBalance: "",
    staleOnly: false,
    sort: "default",
  });

  // Dialogs
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [depositFor, setDepositFor] = useState(null);
  const [depositAmount, setDepositAmount] = useState("");
  const [depositCashappId, setDepositCashappId] = useState("");
  const [withdrawFor, setWithdrawFor] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawRate, setWithdrawRate] = useState("");
  const [withdrawNotes, setWithdrawNotes] = useState("");
  const [updateBalanceFor, setUpdateBalanceFor] = useState(null);
  const [updateBalanceValue, setUpdateBalanceValue] = useState("");
  const [updateBalanceNotes, setUpdateBalanceNotes] = useState("");
  const [historyFor, setHistoryFor] = useState(null);
  const [optsOpen, setOptsOpen] = useState(false);
  const [spendingEditorFor, setSpendingEditorFor] = useState(null);
  const [spendingDraft, setSpendingDraft] = useState("");
  const [optsDraft, setOptsDraft] = useState({
    groups: [],
    locations: [],
    number_locations: [],
    statuses: [],
  });

  const load = async (options = {}) => {
    const showLoading = options.showLoading ?? !loadedOnceRef.current;
    if (showLoading) setLoading(true);
    try {
      const params = { type: "chime" };
      if (filters.q) params.q = filters.q;
      if (filters.cashappLocation) params.linked_cashapp_location = filters.cashappLocation;
      if (filters.group !== "all") params.group = filters.group;
      if (filters.location !== "all") params.location = filters.location;
      if (filters.status !== "all") params.status = filters.status;
      if (filters.minBalance !== "") params.min_balance = Number(filters.minBalance);
      if (filters.staleOnly) params.not_updated_days = 7;
      const [w, o] = await Promise.all([
        api.get("/wallets", { params }),
        api.get("/chime-options").catch(() => ({
          data: {
            groups: [],
            locations: [],
            number_locations: [],
            statuses: ["active"],
          },
        })),
      ]);
      setList(w.data);
      setOpts(o.data);
    } finally {
      loadedOnceRef.current = true;
      setLoading(false);
    }
  };

  useEffect(() => {
    load(); // eslint-disable-next-line
  }, [filters]);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, status: opts.statuses?.[0] || "active" });
    setOpen(true);
  };
  const openEdit = (w) => {
    setEditing(w);
    setForm({ ...EMPTY, ...w });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        balance: Number(form.balance) || 0,
        spending_limit: Number(form.spending_limit) || 0,
        hit_limit: Number(form.spending_limit) >= 5000 ? true : !!form.hit_limit,
        type: "chime",
      };
      if (editing) {
        await api.put(`/wallets/${editing.id}`, payload);
        toast.success("Chime updated");
      } else {
        await api.post("/wallets", payload);
        toast.success("Chime added");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete chime "${w.name}"? Linked CashApps will be unlinked.`))
      return;
    try {
      await api.delete(`/wallets/${w.id}`);
      toast.success("Chime deleted");
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const toggleHitLimit = async (w) => {
    try {
      await api.put(`/wallets/${w.id}`, { hit_limit: !w.hit_limit });
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const openSpendingEditor = (w) => {
    setSpendingEditorFor(w);
    setSpendingDraft(String(Number(w.spending_limit || 0) || ""));
  };

  const saveSpendingLimit = async (amount) => {
    if (!spendingEditorFor) return;
    const value = Math.max(0, Number(amount) || 0);
    try {
      await api.put(`/wallets/${spendingEditorFor.id}`, {
        spending_limit: value,
        hit_limit: value >= 5000,
      });
      toast.success("Spending limit updated");
      setSpendingEditorFor(null);
      setSpendingDraft("");
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  // === Add Cash to linked CashApp ===
  const openAddCash = (chime) => {
    const linked = chime.linked_cashapps || [];
    setDepositFor(chime);
    setDepositCashappId(linked[0]?.id || "");
    setDepositAmount("");
  };
  const submitAddCash = async () => {
    try {
      if (!depositCashappId) {
        toast.error("Link a CashApp to this Chime first");
        return;
      }
      const amt = Number(depositAmount);
      if (!amt || amt <= 0) {
        toast.error("Enter a valid amount");
        return;
      }
      await api.post("/transactions", {
        wallet_id: depositFor.id,
        kind: "deposit",
        amount_usd: amt,
        target_wallet_id: depositCashappId,
        notes: `From Chime · ${depositFor.name}`,
      });
      toast.success("Cash added to CashApp");
      setDepositFor(null);
      setDepositAmount("");
      setDepositCashappId("");
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  // === Withdraw from chime ===
  const openWithdraw = async (chime) => {
    setWithdrawFor(chime);
    setWithdrawAmount("");
    setWithdrawNotes("");
    try {
      const r = await api.get("/rates");
      const ch = r.data.find((x) => x.wallet_type === "chime");
      setWithdrawRate(ch?.rate ?? 1);
    } catch {
      setWithdrawRate(1);
    }
  };
  const submitWithdraw = async () => {
    try {
      const amt = Number(withdrawAmount);
      if (!amt || amt <= 0) {
        toast.error("Enter a valid amount");
        return;
      }
      await api.post("/transactions", {
        wallet_id: withdrawFor.id,
        kind: "withdrawal",
        amount_usd: amt,
        rate: Number(withdrawRate) || null,
        notes: withdrawNotes,
      });
      toast.success("Withdrawal recorded");
      setWithdrawFor(null);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  // === Update balance manually ===
  const openUpdateBalance = (chime) => {
    setUpdateBalanceFor(chime);
    setUpdateBalanceValue(String(chime.balance ?? 0));
    setUpdateBalanceNotes("");
  };
  const submitUpdateBalance = async () => {
    try {
      const nb = Number(updateBalanceValue);
      if (Number.isNaN(nb)) {
        toast.error("Enter a valid balance");
        return;
      }
      await api.post("/transactions", {
        wallet_id: updateBalanceFor.id,
        kind: "balance_update",
        new_balance: nb,
        notes: updateBalanceNotes,
      });
      toast.success("Balance updated");
      setUpdateBalanceFor(null);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  // Settings dialog
  const openOpts = () => {
    setOptsDraft({
      groups: [...(opts.groups || [])],
      locations: [...(opts.locations || [])],
      number_locations: [...(opts.number_locations || [])],
      statuses: [...(opts.statuses || [])],
    });
    setOptsOpen(true);
  };
  const saveOpts = async () => {
    try {
      const r = await api.put("/chime-options", optsDraft);
      setOpts(r.data);
      toast.success("Options updated");
      setOptsOpen(false);
      load();
    } catch (e) {
      toast.error(apiErrorText(e));
    }
  };

  const totalBalance = useMemo(
    () => list.reduce((s, w) => s + (w.balance || 0), 0),
    [list]
  );

  const activeFilterCount =
    (filters.q ? 1 : 0) +
    (filters.cashappLocation ? 1 : 0) +
    (filters.group !== "all" ? 1 : 0) +
    (filters.location !== "all" ? 1 : 0) +
    (filters.status !== "all" ? 1 : 0) +
    (filters.minBalance !== "" ? 1 : 0) +
    (filters.staleOnly ? 1 : 0) +
    (filters.sort !== "default" ? 1 : 0);

  const spendingStats = useMemo(() => {
    const withLimit = list.filter((w) => Number(w.spending_limit || 0) > 0);
    return {
      total: withLimit.length,
      hit: withLimit.filter((w) => w.hit_limit).length,
      amount: withLimit.reduce((sum, w) => sum + Number(w.spending_limit || 0), 0),
    };
  }, [list]);

  // Sort the list according to the selected sort. Default = group-size ordered.
  const sortedList = useMemo(() => {
    if (filters.sort === "name-asc") {
      return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    }
    if (filters.sort === "name-desc") {
      return [...list].sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    }
    if (filters.sort === "balance-desc") {
      return [...list].sort((a, b) => (b.balance || 0) - (a.balance || 0));
    }
    if (filters.sort === "balance-asc") {
      return [...list].sort((a, b) => (a.balance || 0) - (b.balance || 0));
    }
    if (filters.sort === "spending-desc") {
      return [...list].sort((a, b) => Number(b.spending_limit || 0) - Number(a.spending_limit || 0));
    }
    if (filters.sort === "spending-asc") {
      return [...list].sort((a, b) => Number(a.spending_limit || 0) - Number(b.spending_limit || 0));
    }
    const sizeMap = new Map();
    list.forEach((w) => {
      const g = (w.group || "").trim();
      sizeMap.set(g, (sizeMap.get(g) || 0) + 1);
    });
    return [...list].sort((a, b) => {
      const ga = (a.group || "").trim();
      const gb = (b.group || "").trim();
      if (!!ga !== !!gb) return ga ? -1 : 1; // grouped first
      if (ga !== gb) {
        const diff = (sizeMap.get(gb) || 0) - (sizeMap.get(ga) || 0);
        if (diff !== 0) return diff;
        return ga.localeCompare(gb);
      }
      return (b.created_at || "").localeCompare(a.created_at || "");
    });
  }, [list, filters.sort]);

  const isStale = (iso) => {
    if (!iso) return true;
    return Date.now() - new Date(iso).getTime() > 7 * 24 * 60 * 60 * 1000;
  };
  const staleCount = useMemo(() => list.filter((w) => isStale(w.updated_at)).length, [list]);

  return (
    <Layout
      title="Chimes"
      subtitle="Track Chime accounts by group, location, status & balance"
      testId="chimes-page"
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={openOpts}
            className="h-9"
            data-testid="manage-options-btn"
          >
            <Settings2 size={14} className="mr-1.5" /> Manage options
          </Button>
          <Button
            onClick={openCreate}
            className="h-9 bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="add-chime-btn"
          >
            <Plus size={14} className="mr-1.5" /> Add Chime
          </Button>
        </div>
      }
    >
      {/* Spending limit counters */}
      {spendingStats.total > 0 && (
        <div className="mb-5 stat-card p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-display font-bold tracking-tight text-[15px]">
                Spending limits hit
              </div>
              <div className="text-[11.5px] text-zinc-500 mt-0.5">
                $5,000 or more marks a Chime as hit. These reset to $0 each new month.
              </div>
            </div>
            <div className="text-[12px] text-zinc-500">
              <span
                className={`font-mono font-medium ${
                  spendingStats.hit > 0 ? "text-rose-600" : "text-emerald-700"
                }`}
              >
                {spendingStats.hit}
              </span>
              <span className="mx-1">/</span>
              <span className="font-mono">{spendingStats.total}</span>
              <span className="ml-1">total</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-[12.5px]">
            <div className="rounded-md border hairline bg-white px-3 py-1.5">
              Total spending limits: <span className="font-mono">{fmtUSD(spendingStats.amount)}</span>
            </div>
            <button
              onClick={() => setFilters({ ...filters, sort: "spending-desc" })}
              className="rounded-md border hairline bg-white px-3 py-1.5 hover:bg-zinc-50"
            >
              Sort high to low
            </button>
            <button
              onClick={() => setFilters({ ...filters, sort: "spending-asc" })}
              className="rounded-md border hairline bg-white px-3 py-1.5 hover:bg-zinc-50"
            >
              Sort low to high
            </button>
          </div>
        </div>
      )}

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Chimes (filtered)
          </div>
          <div className="font-mono text-[22px] font-medium mt-1">
            {list.length}
          </div>
        </div>
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Balance
          </div>
          <div className="font-mono text-[22px] font-medium mt-1">
            {fmtUSD(totalBalance)}
          </div>
        </div>
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Groups
          </div>
          <div className="font-mono text-[22px] font-medium mt-1">
            {opts.groups?.length || 0}
          </div>
        </div>
        <div className="stat-card p-4">
          <div className="text-[10.5px] uppercase tracking-widest text-zinc-500">
            Locations
          </div>
          <div className="font-mono text-[22px] font-medium mt-1">
            {opts.locations?.length || 0}
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="stat-card p-3 mb-4 flex flex-wrap items-center gap-2">
	        <div className="relative flex-1 min-w-[220px]">
          <Search
            size={13}
            strokeWidth={1.7}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <Input
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Search name, spending limits, note, email, number"
            className="h-9 pl-8"
            data-testid="chime-search-input"
	          />
	        </div>
	        <Select
          value={filters.group}
          onValueChange={(v) => setFilters({ ...filters, group: v })}
        >
          <SelectTrigger className="h-9 w-[150px]" data-testid="chime-filter-group">
            <SelectValue placeholder="Group" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All groups</SelectItem>
            {opts.groups?.map((g) => (
              <SelectItem key={g} value={g}>
                {g}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.location}
          onValueChange={(v) => setFilters({ ...filters, location: v })}
        >
          <SelectTrigger className="h-9 w-[150px]" data-testid="chime-filter-location">
            <SelectValue placeholder="Location" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All locations</SelectItem>
            {opts.locations?.map((g) => (
              <SelectItem key={g} value={g}>
                {g}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.status}
          onValueChange={(v) => setFilters({ ...filters, status: v })}
        >
          <SelectTrigger className="h-9 w-[140px]" data-testid="chime-filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {opts.statuses?.map((g) => (
              <SelectItem key={g} value={g}>
                {g}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1">
          <span className="text-[12px] text-zinc-500 ml-1">Bal ≥</span>
          <Input
            type="number"
            step="1"
            value={filters.minBalance}
            onChange={(e) => setFilters({ ...filters, minBalance: e.target.value })}
            className="h-9 w-[100px]"
            placeholder="500"
            data-testid="chime-filter-min-balance"
          />
        </div>
        <Select
          value={filters.sort}
          onValueChange={(v) => setFilters({ ...filters, sort: v })}
        >
          <SelectTrigger className="h-9 w-[170px]" data-testid="chime-filter-sort">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Sort: by group</SelectItem>
            <SelectItem value="name-asc">Name: A → Z</SelectItem>
            <SelectItem value="name-desc">Name: Z → A</SelectItem>
            <SelectItem value="balance-desc">Balance: high → low</SelectItem>
            <SelectItem value="balance-asc">Balance: low → high</SelectItem>
            <SelectItem value="spending-desc">Spending limits: high → low</SelectItem>
            <SelectItem value="spending-asc">Spending limits: low → high</SelectItem>
          </SelectContent>
        </Select>
        <button
          onClick={() => setFilters({ ...filters, staleOnly: !filters.staleOnly })}
          className={`h-9 inline-flex items-center gap-1.5 rounded-md border px-3 text-[12.5px] transition-colors ${
            filters.staleOnly
              ? "bg-amber-50 border-amber-300 text-amber-800"
              : "hairline bg-white text-zinc-700 hover:bg-zinc-50"
          }`}
          data-testid="chime-filter-stale"
          title="Show chimes not updated in 7+ days"
        >
          Not updated 7d+
          {staleCount > 0 && (
            <span className="font-mono text-[11px] px-1.5 py-px rounded bg-amber-200 text-amber-900">
              {staleCount}
            </span>
          )}
        </button>
        {activeFilterCount > 0 && (
          <Button
            variant="ghost"
            className="h-9 text-zinc-600"
            onClick={() =>
              setFilters({
	                q: "",
	                cashappLocation: "",
	                group: "all",
                location: "all",
                status: "all",
                minBalance: "",
                staleOnly: false,
                sort: "default",
              })
            }
            data-testid="chime-clear-filters"
          >
            <X size={13} className="mr-1" /> Clear
          </Button>
        )}
      </div>

      <div className="stat-card p-3 mb-4 flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            size={13}
            strokeWidth={1.7}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"
          />
          <Input
            value={filters.cashappLocation}
            onChange={(e) =>
              setFilters({ ...filters, cashappLocation: e.target.value })
            }
            placeholder="Search Chimes by linked CashApp location"
            className="h-9 pl-8"
            data-testid="chime-cashapp-location-search-bar"
          />
        </div>
      </div>

      {/* Table */}
      <div className="stat-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-[13px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-zinc-500 border-b hairline bg-zinc-50/50">
                <th className="py-3 px-4 font-medium">Name</th>
                <th className="py-3 px-4 font-medium">Group</th>
                <th className="py-3 px-4 font-medium">Location</th>
                <th className="py-3 px-4 font-medium">Spending limits</th>
	                <th className="py-3 px-4 font-medium">Card · last 4</th>
	                <th className="py-3 px-4 font-medium">Linked CashApp</th>
	                <th className="py-3 px-4 font-medium">CashApp locations</th>
	                <th className="py-3 px-4 font-medium">Status</th>
	                <th className="py-3 px-4 font-medium text-right">Balance</th>
	                <th className="py-3 px-4 font-medium">Note</th>
	                <th className="py-3 px-4 font-medium text-right">Actions</th>
	              </tr>
            </thead>
            <tbody className="divide-y hairline">
              {loading ? (
                <tr>
                  <td colSpan={11} className="py-8 text-center text-zinc-500">
                    Loading…
                  </td>
                </tr>
              ) : sortedList.length === 0 ? (
                <tr>
                  <td colSpan={11} className="py-10 text-center text-zinc-500">
                    {activeFilterCount > 0
                      ? "No chimes match these filters."
                      : "No Chime wallets yet — add one to get started."}
                  </td>
                </tr>
              ) : (
                sortedList.map((w) => {
                  const gc = colorFor(w.group);
                  const tone = STATUS_TONE[(w.status || "").toLowerCase()] ||
                    "bg-zinc-100 border-zinc-200 text-zinc-600";
                  return (
                    <tr
                      key={w.id}
                      className={`row-hover ${
                        w.hit_limit
                          ? "bg-rose-50/50"
                          : isStale(w.updated_at)
                          ? "bg-amber-50/40"
                          : ""
                      }`}
                      data-testid={`chime-row-${w.id}`}
                    >
                      <td className="py-3 px-4 font-medium">
                        <span
                          className={
                            w.hit_limit
                              ? "text-rose-700 flex items-center gap-1.5"
                              : ""
                          }
                        >
                          {w.hit_limit && (
                            <span className="inline-block h-1.5 w-1.5 rounded-full bg-rose-500" />
                          )}
                          {w.name}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {w.group ? (
                          <Badge
                            variant="outline"
                            className={`text-[11px] ${gc?.bg} ${gc?.border} ${gc?.text}`}
                          >
                            {w.group}
                          </Badge>
                        ) : (
                          <span className="text-zinc-400 text-[12px]">—</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-zinc-700">{w.location || "—"}</td>
                      <td className="py-3 px-4 text-zinc-700 font-mono text-[12.5px]">
                        {Number(w.spending_limit || 0) > 0 ? (
                          <span className="inline-flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => openSpendingEditor(w)}
                              className="font-mono text-zinc-800 underline-offset-2 hover:underline"
                              data-testid={`edit-spending-limit-${w.id}`}
                              title="Edit spending limit"
                            >
                              {fmtUSD(w.spending_limit)}
                            </button>
                            <span className="inline-flex flex-col items-start gap-0.5">
                              <button
                                onClick={() => toggleHitLimit(w)}
                                className={`text-[10px] font-sans px-1.5 py-px rounded tracking-wide transition-colors ${
                                  w.hit_limit
                                    ? "bg-rose-600 text-white hover:bg-rose-700"
                                    : "bg-zinc-100 text-zinc-500 hover:bg-zinc-200"
                                }`}
                                data-testid={`toggle-hit-${w.id}`}
                                title={w.hit_limit ? "Click to unmark" : "Mark as hit"}
                              >
                                {w.hit_limit ? "HIT" : "Mark hit"}
                              </button>
                              {w.hit_limit && w.hit_limit_at && (
                                <span
                                  className="text-[10px] font-sans text-rose-700 leading-none"
                                  data-testid={`hit-date-${w.id}`}
                                >
                                  {fmtDateShort(w.hit_limit_at)}
                                </span>
                              )}
                            </span>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openSpendingEditor(w)}
                            className="font-mono text-zinc-400 underline-offset-2 hover:text-zinc-700 hover:underline"
                            data-testid={`edit-spending-limit-${w.id}`}
                            title="Set spending limit"
                          >
                            $0
                          </button>
                        )}
                      </td>
                      <td className="py-3 px-4 font-mono text-[12.5px]">
                        {w.card_number ? (
                          <span className="text-zinc-700">
                            •••• {String(w.card_number).slice(-4)}
                          </span>
                        ) : (
                          <span className="text-zinc-400">—</span>
                        )}
                      </td>
	                      <td className="py-3 px-4">
	                        {w.linked_cashapps && w.linked_cashapps.length > 0 ? (
	                          <div className="flex flex-wrap gap-1">
	                            {w.linked_cashapps.map((c) => (
                              <span
                                key={c.id}
                                className="inline-flex items-center gap-1 text-[11.5px] text-zinc-700 border hairline rounded px-1.5 py-0.5"
	                              >
		                                <Link2 size={11} strokeWidth={1.7} />
		                                {c.name}
		                              </span>
	                            ))}
                          </div>
                        ) : (
	                          <span className="text-zinc-400 text-[12px]">—</span>
	                        )}
	                      </td>
	                      <td className="py-3 px-4">
		                        {w.linked_cashapps?.some((c) => c.location || c.number_location) ? (
		                          <div className="flex flex-wrap gap-1">
		                            {w.linked_cashapps
		                              .filter((c) => c.location || c.number_location)
		                              .map((c) => (
		                                <span
		                                  key={`${c.id}-loc`}
	                                  className="inline-flex items-center gap-1 text-[11.5px] text-sky-800 border border-sky-200 bg-sky-50 rounded px-1.5 py-0.5"
	                                  title={c.name}
	                                >
	                                  {c.location || "No location"}
		                                  {c.number_location && (
		                                    <span className="font-medium text-sky-600">
		                                      · {c.number_location}
		                                    </span>
		                                  )}
			                                </span>
		                              ))}
	                          </div>
	                        ) : (
	                          <span className="text-zinc-400 text-[12px]">—</span>
	                        )}
	                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-flex text-[11.5px] px-1.5 py-0.5 rounded border capitalize ${tone}`}
                        >
                          {w.status || "—"}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-right">
                        <button
                          type="button"
                          onClick={() => openUpdateBalance(w)}
                          className="font-mono text-right text-zinc-800 underline-offset-2 hover:text-sky-800 hover:underline"
                          title="Update balance"
                          data-testid={`quick-update-balance-${w.id}`}
                        >
                          {fmtUSD(w.balance)}
                        </button>
                      </td>
	                      <td className="py-3 px-4 text-[12px] text-zinc-600 max-w-[200px] truncate">
	                        {w.details || "—"}
	                      </td>
	                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"
                                title="Quick actions"
                                data-testid={`actions-chime-${w.id}`}
                              >
                                <MoreHorizontal size={15} strokeWidth={1.7} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56">
                              <DropdownMenuItem
                                onClick={() => openAddCash(w)}
                                data-testid={`add-cash-chime-${w.id}`}
                              >
                                <ArrowDownLeft size={13} className="mr-2" /> Add cash → CashApp
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openWithdraw(w)}
                                data-testid={`withdraw-chime-${w.id}`}
                              >
                                <ArrowUpRight size={13} className="mr-2" /> Withdraw
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openUpdateBalance(w)}
                                data-testid={`update-balance-chime-${w.id}`}
                              >
                                <RefreshCw size={13} className="mr-2" /> Update balance
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => setHistoryFor(w)}
                                data-testid={`history-chime-${w.id}`}
                              >
                                <History size={13} className="mr-2" /> View history
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                          <button
                            onClick={() => openEdit(w)}
                            className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600"
                            title="Edit"
                            data-testid={`edit-chime-${w.id}`}
                          >
                            <Pencil size={14} strokeWidth={1.6} />
                          </button>
                          <button
                            onClick={() => remove(w)}
                            className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                            title="Delete"
                            data-testid={`delete-chime-${w.id}`}
                          >
                            <Trash2 size={14} strokeWidth={1.6} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Spending limit quick editor */}
      <Dialog
        open={!!spendingEditorFor}
        onOpenChange={(v) => {
          if (!v) {
            setSpendingEditorFor(null);
            setSpendingDraft("");
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="spending-limit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Set spending limit</DialogTitle>
            <DialogDescription>
              {spendingEditorFor?.name || "Chime"} will be marked hit at $5,000 or more.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>Custom amount</Label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={spendingDraft}
              onChange={(e) => setSpendingDraft(e.target.value)}
              placeholder="0.00"
              data-testid="spending-limit-custom-input"
            />
            <div className="text-[11px] text-zinc-500">
              Use $0 to clear the monthly spending limit.
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => saveSpendingLimit(0)}
              data-testid="spending-limit-clear"
            >
              Set $0
            </Button>
            <Button
              type="button"
              onClick={() => saveSpendingLimit(spendingDraft)}
              data-testid="spending-limit-save"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create / Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="chime-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Edit Chime" : "Add Chime"}
            </DialogTitle>
            <DialogDescription>
              Track group, location, owner and card linkage.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label>Chime Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Chime – John"
                data-testid="chime-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Group</Label>
              <Select
                value={form.group || "none"}
                onValueChange={(v) => setForm({ ...form, group: v === "none" ? "" : v })}
              >
                <SelectTrigger data-testid="chime-group-select">
                  <SelectValue placeholder="Select group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No group</SelectItem>
                  {opts.groups?.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Location</Label>
              <Select
                value={form.location || "none"}
                onValueChange={(v) =>
                  setForm({ ...form, location: v === "none" ? "" : v })
                }
              >
                <SelectTrigger data-testid="chime-location-select">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No location</SelectItem>
                  {opts.locations?.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Spending limits (USD)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.spending_limit || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  const amount = Number(value) || 0;
                  setForm({
                    ...form,
                    spending_limit: value,
                    hit_limit: amount >= 5000 ? true : form.hit_limit,
                  });
                }}
                placeholder="0.00"
                data-testid="chime-spending-limit-input"
              />
              <div className="text-[11px] text-zinc-500">
                $5,000 or more is automatically marked as hit.
              </div>
            </div>
            <div className="col-span-2 flex items-center justify-between rounded-md border hairline px-3 py-2.5">
              <div>
                <div className="text-[13px] font-medium">Hit spending limit</div>
                <div className="text-[11.5px] text-zinc-500">
                  Mark on when this chime has reached its spending limit
                </div>
              </div>
              <Switch
                checked={!!form.hit_limit}
                onCheckedChange={(v) => setForm({ ...form, hit_limit: v })}
                data-testid="chime-hit-limit-toggle"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select
                value={form.status}
                onValueChange={(v) => setForm({ ...form, status: v })}
              >
                <SelectTrigger data-testid="chime-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {opts.statuses?.map((g) => (
                    <SelectItem key={g} value={g} className="capitalize">
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email || ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="user@example.com"
                data-testid="chime-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Number</Label>
              <Input
                value={form.number || ""}
                onChange={(e) => setForm({ ...form, number: e.target.value })}
                placeholder="Account / phone"
                data-testid="chime-number-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Number location</Label>
              <Select
                value={form.number_location || "none"}
                onValueChange={(v) =>
                  setForm({
                    ...form,
                    number_location: v === "none" ? "" : v,
                  })
                }
              >
                <SelectTrigger data-testid="chime-number-location-select">
                  <SelectValue placeholder="Select number location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No number location</SelectItem>
                  {opts.number_locations?.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Card last 4 digits</Label>
              <Input
                value={form.card_number || ""}
                onChange={(e) => setForm({ ...form, card_number: e.target.value })}
                placeholder="e.g. 4242"
                maxLength={4}
                inputMode="numeric"
                data-testid="chime-card-number-input"
              />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Opening balance (USD)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.balance}
                onChange={(e) => setForm({ ...form, balance: e.target.value })}
                disabled={!!editing}
                data-testid="chime-balance-input"
              />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Note</Label>
              <Textarea
                value={form.details || ""}
                onChange={(e) => setForm({ ...form, details: e.target.value })}
                placeholder="Anything you want to track — recovery email, IDs, etc."
                rows={3}
                data-testid="chime-details-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={save}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="save-chime-btn"
            >
              {editing ? "Save changes" : "Add Chime"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add cash → linked CashApp */}
      <Dialog open={!!depositFor} onOpenChange={(v) => !v && setDepositFor(null)}>
        <DialogContent data-testid="add-cash-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add cash to CashApp</DialogTitle>
            <DialogDescription>
              From <span className="font-medium">{depositFor?.name}</span>. The amount
              will be deposited into the linked CashApp.
            </DialogDescription>
          </DialogHeader>
          {depositFor?.linked_cashapps?.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 space-y-1.5">
                <Label>Linked CashApp</Label>
                <Select
                  value={depositCashappId}
                  onValueChange={setDepositCashappId}
                >
                  <SelectTrigger data-testid="add-cash-cashapp-select">
                    <SelectValue placeholder="Select CashApp" />
                  </SelectTrigger>
                  <SelectContent>
                    {depositFor.linked_cashapps.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 space-y-1.5">
                <Label>Amount (USD)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={depositAmount}
                  onChange={(e) => setDepositAmount(e.target.value)}
                  data-testid="add-cash-amount-input"
                />
              </div>
            </div>
          ) : (
            <div className="rounded-md border hairline bg-amber-50/60 px-3 py-3 text-[13px] text-amber-800">
              No CashApp linked to this Chime yet. Open the CashApp page and link one
              first.
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDepositFor(null)}>
              Cancel
            </Button>
            <Button
              onClick={submitAddCash}
              disabled={!depositFor?.linked_cashapps?.length}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="confirm-add-cash-btn"
            >
              Add cash
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Withdraw */}
      <Dialog open={!!withdrawFor} onOpenChange={(v) => !v && setWithdrawFor(null)}>
        <DialogContent data-testid="withdraw-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Withdraw from Chime</DialogTitle>
            <DialogDescription>
              {withdrawFor?.name} · current balance{" "}
              <span className="font-mono">{fmtUSD(withdrawFor?.balance || 0)}</span>
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Amount (USD) *</Label>
              <Input
                type="number"
                step="0.01"
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                data-testid="withdraw-amount-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Rate (USD → USDT)</Label>
              <Input
                type="number"
                step="0.0001"
                value={withdrawRate}
                onChange={(e) => setWithdrawRate(e.target.value)}
                data-testid="withdraw-rate-input"
              />
              <div className="text-[11px] text-zinc-500 font-mono">
                ≈ {fmtUSD(Number(withdrawAmount || 0))} →{" "}
                {(Number(withdrawAmount || 0) * Number(withdrawRate || 0)).toFixed(4)} USDT
              </div>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={withdrawNotes}
                onChange={(e) => setWithdrawNotes(e.target.value)}
                placeholder="Optional"
                data-testid="withdraw-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWithdrawFor(null)}>
              Cancel
            </Button>
            <Button
              onClick={submitWithdraw}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="confirm-withdraw-btn"
            >
              <ArrowUpRight size={13} className="mr-1" /> Record withdrawal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update balance */}
      <Dialog
        open={!!updateBalanceFor}
        onOpenChange={(v) => !v && setUpdateBalanceFor(null)}
      >
        <DialogContent data-testid="update-balance-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Update balance</DialogTitle>
            <DialogDescription>
              Set the current balance for {updateBalanceFor?.name}. Use this after add-cash
              and withdrawals to record the leftover amount.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Current balance</Label>
              <Input
                value={fmtUSD(updateBalanceFor?.balance || 0)}
                disabled
                className="font-mono"
              />
            </div>
            <div className="space-y-1.5">
              <Label>New balance (USD) *</Label>
              <Input
                type="number"
                step="0.01"
                value={updateBalanceValue}
                onChange={(e) => setUpdateBalanceValue(e.target.value)}
                data-testid="update-balance-value-input"
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Input
                value={updateBalanceNotes}
                onChange={(e) => setUpdateBalanceNotes(e.target.value)}
                placeholder="e.g. End-of-day reconcile"
                data-testid="update-balance-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateBalanceFor(null)}>
              Cancel
            </Button>
            <Button
              onClick={submitUpdateBalance}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="confirm-update-balance-btn"
            >
              <RefreshCw size={13} className="mr-1" /> Save balance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History drawer */}
      <WalletHistorySheet
        wallet={historyFor}
        onClose={() => setHistoryFor(null)}
        onMutate={load}
      />

      {/* Manage Options */}
      <Dialog open={optsOpen} onOpenChange={setOptsOpen}>
        <DialogContent className="max-w-2xl" data-testid="chime-options-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Manage options</DialogTitle>
            <DialogDescription>
              Define your own list of groups, locations, number locations and statuses.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { key: "groups", label: "Groups", placeholder: "e.g. Team A" },
              { key: "locations", label: "Locations", placeholder: "e.g. Miami, FL" },
              { key: "number_locations", label: "Number locations", placeholder: "e.g. NY number" },
              { key: "statuses", label: "Statuses", placeholder: "e.g. active" },
            ].map(({ key, label, placeholder }) => (
              <OptionList
                key={key}
                label={label}
                values={optsDraft[key]}
                placeholder={placeholder}
                onChange={(vals) => setOptsDraft({ ...optsDraft, [key]: vals })}
                testKey={key}
              />
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOptsOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={saveOpts}
              className="bg-zinc-900 hover:bg-zinc-800 text-white"
              data-testid="save-chime-options-btn"
            >
              Save options
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function OptionList({ label, values, placeholder, onChange, testKey }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (values.some((x) => x.toLowerCase() === v.toLowerCase())) {
      toast.message(`"${v}" already exists`);
      return;
    }
    onChange([...values, v]);
    setDraft("");
  };
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  return (
    <div className="border hairline rounded-md p-3">
      <div className="text-[12px] font-medium text-zinc-700 mb-2">{label}</div>
      <div className="flex gap-1.5 mb-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())}
          placeholder={placeholder}
          className="h-8"
          data-testid={`option-input-${testKey}`}
        />
        <Button
          onClick={add}
          className="h-8 bg-zinc-900 hover:bg-zinc-800 text-white"
          data-testid={`option-add-${testKey}`}
        >
          <Plus size={13} />
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5 max-h-44 overflow-y-auto thin-scroll">
        {values.length === 0 ? (
          <span className="text-[12px] text-zinc-400">No items yet</span>
        ) : (
          values.map((v, i) => (
            <span
              key={`${v}-${i}`}
              className="inline-flex items-center gap-1 text-[11.5px] border hairline rounded px-1.5 py-0.5 bg-zinc-50"
            >
              {v}
              <button
                onClick={() => remove(i)}
                className="text-zinc-400 hover:text-rose-600"
                data-testid={`option-remove-${testKey}-${i}`}
              >
                <X size={11} />
              </button>
            </span>
          ))
        )}
      </div>
    </div>
  );
}
